import { Link } from "react-router-dom";
import { Star, ShoppingCart, Heart } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { getImageUrl } from "../api";
import { useState, useEffect } from "react";

const formatPrice = (price) =>
  new Intl.NumberFormat("fr-SN", { style: "currency", currency: "XOF", minimumFractionDigits: 0 }).format(price);

const StarRating = ({ rating, size = 12 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        size={size}
        className={s <= Math.round(rating) ? "text-[#FF9900] fill-[#FF9900]" : "text-gray-300"}
      />
    ))}
  </div>
);

export { StarRating, formatPrice };

function useFavorites() {
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jkl_favorites") || "[]"); } catch { return []; }
  });
  const toggle = (id) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem("jkl_favorites", JSON.stringify(next));
      return next;
    });
  };
  return { favorites, toggle };
}

export { useFavorites };

export default function ProductCard({ product }) {
  const { addToCart, cart } = useCart();
  const { favorites, toggle } = useFavorites();
  const inCart = cart.some((i) => i.product_id === product._id);
  const isFav = favorites.includes(product._id);
  const imgSrc = getImageUrl(product.images?.[0]) || "https://via.placeholder.com/300x300?text=Pas+d%27image";

  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex flex-col relative" data-testid="product-card">
      <button
        onClick={(e) => { e.preventDefault(); toggle(product._id); }}
        className={`absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center shadow-sm transition-all ${isFav ? "bg-red-50" : "bg-white/80 backdrop-blur-sm"}`}
        data-testid={`favorite-btn-${product._id}`}
        aria-label={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
      >
        <Heart size={14} className={isFav ? "text-red-500 fill-red-500" : "text-gray-400"} />
      </button>
      <Link to={`/produit/${product._id}`} className="block" data-testid={`product-link-${product._id}`}>
        <div className="aspect-square overflow-hidden bg-gray-100">
          <img
            src={imgSrc}
            alt={product.name}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
            loading="lazy"
            onError={(e) => { e.target.src = "https://via.placeholder.com/300x300?text=Image"; }}
          />
        </div>
      </Link>
      <div className="p-2.5 flex flex-col flex-1 gap-1">
        <Link to={`/produit/${product._id}`}>
          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 hover:text-[#FF9900] transition-colors leading-tight" data-testid="product-name">
            {product.name}
          </h3>
        </Link>
        {product.total_reviews > 0 && (
          <div className="flex items-center gap-1">
            <StarRating rating={product.rating} />
            <span className="text-xs text-gray-500">({product.total_reviews})</span>
          </div>
        )}
        {product.vendor_shop_name && (
          <Link to={`/boutique/${product.vendor_id}`} className="text-xs text-blue-600 hover:underline truncate">
            {product.vendor_shop_name}
          </Link>
        )}
        <div className="mt-auto pt-1">
          <p className="text-base font-black text-[#232F3E]" data-testid="product-price">
            {formatPrice(product.price)}
          </p>
          <button
            onClick={() => addToCart(product)}
            disabled={product.stock === 0}
            className={`w-full mt-1.5 text-xs font-bold py-2 rounded-2xl transition-colors ${
              product.stock === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : inCart
                ? "bg-[#16A34A] text-white"
                : "bg-[#F59E0B] text-white hover:bg-[#D97706]"
            }`}
            data-testid="add-to-cart-btn"
          >
            {product.stock === 0 ? "Épuisé" : inCart ? "Ajouté au panier" : "Ajouter au panier"}
          </button>
        </div>
      </div>
    </div>
  );
}
